import React, { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { CheckCircle, CloudUpload, Error } from "@mui/icons-material";
import { Box, Paper, Tooltip, Typography } from "@mui/material";
import classNames from "classnames";
import { groupBy } from "lodash";

import { getAllAcceptedExtensions } from "fond/fileValidation";
import { LayerStatus } from "fond/hooks/useGetImportLayers";
import { GroupLabel } from "fond/project/panels/InputPanel/GroupLabel";
import { addImport } from "fond/redux/imports";
import { ImportStatus, isImportInProgress, MuPowerInputLayerId } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { LinearProgressWithLabel, StackedNavigationButton, useStackedNavigationContext } from "fond/widgets";

import { BulkUploadGroup, matchDroppedFiles } from "./bulkUpload";
import BulkUploadReview from "./BulkUploadReview";
import { muPowerInputLayers } from "./layers";

interface InputPanelProps {
  isReadOnly?: boolean;
  inputLayers: Map<string, LayerStatus>;
}

const getRightAdornment = ({ isError, isRequired, isComplete }: { isError: boolean; isRequired: boolean; isComplete: boolean }) => {
  if (isError) {
    return (
      <Tooltip title="Import failed">
        <Error color="error" sx={{ fontSize: 18 }} />
      </Tooltip>
    );
  }

  if (!isComplete && isRequired) {
    return (
      <Tooltip title="Required">
        <Error color="action" sx={{ fontSize: 18 }} />
      </Tooltip>
    );
  }

  if (isComplete) {
    return <CheckCircle color="success" sx={{ fontSize: 18 }} />;
  }

  return null;
};

const InputPanel: React.FC<InputPanelProps> = ({ isReadOnly, inputLayers }) => {
  const { open } = useStackedNavigationContext();
  const dispatch = useAppDispatch();
  const versionId = useAppSelector((state) => state.project.versionId);
  const [bulkGroups, setBulkGroups] = useState<BulkUploadGroup[] | null>(null);

  const isAnyUploading = Array.from(inputLayers.values()).some((layer) => layer.status && isImportInProgress(layer.status));

  const groupedLayers = useMemo(() => groupBy(muPowerInputLayers, "group"), []);

  const handleDrop = useCallback((accepted: File[]) => {
    if (accepted.length === 0) return;
    const groups = matchDroppedFiles(accepted);
    setBulkGroups(groups);
  }, []);

  const handleCancel = useCallback(() => setBulkGroups(null), []);

  const handleConfirm = useCallback(
    (selections: Array<{ layerId: MuPowerInputLayerId; files: File[] }>) => {
      selections.forEach(({ layerId, files }) => {
        const displayName = muPowerInputLayers.find((l) => l.key === layerId)?.displayName ?? layerId;
        dispatch(addImport({ versionId, layerKey: layerId, displayName, files }));
      });
      setBulkGroups(null);
    },
    [dispatch, versionId]
  );

  const dropDisabled = !!isReadOnly || isAnyUploading || bulkGroups !== null;
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: getAllAcceptedExtensions(),
    multiple: true,
    noClick: true,
    noKeyboard: true,
    disabled: dropDisabled,
    onDropAccepted: handleDrop,
  });

  return (
    <Box {...getRootProps()} sx={{ position: "relative", outline: "none" }} data-testid="mu-power-input-panel">
      <input {...getInputProps()} />

      {bulkGroups !== null ? (
        <BulkUploadReview groups={bulkGroups} inputLayers={inputLayers} onCancel={handleCancel} onConfirm={handleConfirm} />
      ) : (
        <>
          {Object.entries(groupedLayers).map(([groupName, layers], index) => (
            <Box key={groupName}>
              <GroupLabel
                variant="caption"
                className={classNames({
                  first: index === 0,
                })}
              >
                {groupName}
              </GroupLabel>
              {layers.map(({ key, icon, title, isRequired }) => {
                const layerStatus = inputLayers.get(key);
                const hasLayer = !!layerStatus?.layer;
                const isError = layerStatus?.status === ImportStatus.ERROR;
                const isComplete = hasLayer && layerStatus?.status === ImportStatus.COMPLETE;

                return (
                  <StackedNavigationButton
                    key={`mu-power-${key}-input`}
                    data-testid={`mu-power-${key.split("/").pop()}-input`}
                    icon={icon}
                    title={title}
                    onClick={() => open(key)}
                    sx={{ m: 0, mt: 1 }}
                    disabled={isReadOnly || isAnyUploading}
                    checked={isComplete}
                    rightElement={getRightAdornment({ isError, isComplete, isRequired: !!isRequired })}
                  />
                );
              })}
            </Box>
          ))}

          {isAnyUploading && (
            <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
              <LinearProgressWithLabel color="primary" variant="indeterminate" title="Importing layer" subtext="Processing..." />
            </Paper>
          )}
        </>
      )}

      {isDragActive && !dropDisabled && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            border: (theme) => `2px dashed ${theme.palette.primary.main}`,
            borderRadius: "4px",
            backgroundColor: (theme) => theme.palette.background.paper,
            opacity: 0.95,
            zIndex: 1,
            pointerEvents: "none",
          }}
          data-testid="mu-power-bulk-drop-overlay"
        >
          <CloudUpload color="primary" sx={{ fontSize: 32, mb: 1 }} />
          <Typography variant="body2" fontWeight={500}>
            Drop files to upload layers
          </Typography>
          <Typography variant="caption" color="text.secondary">
            SHAPE, TAB, KML, and GEOJSON files
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default InputPanel;
